// src/admin/components/HomePageConfig/CustomerReviewSection.jsx
import React, { useState } from "react";
import ColorSection from "./ColorSection";
import ColorPicker from "./ColorPicker";
import FontSizeSection from "./FontSizeSection";

export default function CustomerReviewSection({ 
  title = "Customer Reviews", 
  data = {}, 
  setData,
  customerReviewColors = {},
  setCustomerReviewColors,
  uploadImageFile, 
  deleteImageFile, 
  imagesBase 
}) {
  
  // Provide safe defaults
  const safeData = {
    image: "",
    backgroundColor: "#ffffff",
    overlayOpacity: 0.3,
    reviews: [],
    sectionTitle: "Customer Reviews",
    sectionDescription: "What our customers say about us",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
    ...data
  };

  const safeCustomerReviewColors = {
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
    reviewerNameColor: "#000000",
    reviewerDesignationColor: "#666666",
    ratingColor: "#FFD700",
    ...customerReviewColors
  };

  const [localReviews, setLocalReviews] = useState(
    safeData.reviews.map(review => ({ 
      _id: Math.random().toString(36).slice(2), 
      name: "",
      designation: "",
      description: "",
      rating: "5",
      ...review 
    }))
  );

  const handleColorChange = (property, value) => {
    if (setCustomerReviewColors) {
      setCustomerReviewColors(prev => ({
        ...(prev || {}),
        [property]: value === "transparent" ? "transparent" : value
      }));
    }
  };

  const toggleSectionColors = (useSectionColors) => {
    setData(prev => ({
      ...(prev || {}),
      useSectionColors
    }));
  };

  const toggleSectionFontSizes = (useSectionFontSizes) => {
    setData((prev) => ({
      ...(prev || {}),
      useSectionFontSizes,
    }));
  };

  const handleFontSizeChange = (key, value) => {
    setData((prev) => ({
      ...(prev || {}),
      fontSizes: {
        ...(prev?.fontSizes || {}),
        [key]: value,
      },
    }));
  };

  const addReview = () => {
    const newReview = {
      _id: Math.random().toString(36).slice(2),
      name: "",
      designation: "",
      description: "",
      rating: "5"
    };
    setLocalReviews([...localReviews, newReview]);
  };

  const removeReview = (id) => {
    setLocalReviews(localReviews.filter(review => review._id !== id));
  };

  const updateReview = (id, field, value) => {
    setLocalReviews(localReviews.map(review => 
      review._id === id ? { ...review, [field]: value } : review
    ));
  };

  const handleBackgroundImageUpload = async (file) => {
    if (!file) return;
    const filename = await uploadImageFile(file);
    if (filename) {
      setData(prev => ({ ...prev, image: filename }));
    }
  };

  const handleBackgroundImageDelete = async () => {
    if (!safeData.image) return;
    const ok = await deleteImageFile(safeData.image);
    if (ok) {
      setData(prev => ({ ...prev, image: "" }));
    }
  };

  // Update parent data when local reviews change
  React.useEffect(() => {
    setData(prev => ({
      ...prev,
      reviews: localReviews.map(({ _id, ...rest }) => rest)
    }));
  }, [localReviews, setData]);

  return (
    <div className="hcfg-card">
      <div className="hcfg-headrow">
        <h3>{title}</h3>
      </div>

      {/* Section Title & Description */}
      <div className="hcfg-row">
        <div className="hcfg-col">
          <label>Section Title</label>
          <input
            value={safeData.sectionTitle}
            onChange={(e) => setData(prev => ({ ...prev, sectionTitle: e.target.value }))}
            placeholder="Customer Reviews"
            style={{ width: "100%", padding: "8px" }}
          />
        </div>
        <div className="hcfg-col">
          <label>Section Description</label>
          <textarea
            rows={2}
            value={safeData.sectionDescription}
            onChange={(e) => setData(prev => ({ ...prev, sectionDescription: e.target.value }))}
            placeholder="What our customers say about us"
            style={{ width: "100%", padding: "8px", resize: "vertical" }}
          />
        </div>
      </div>

      {/* Background Image & Color */}
      <div className="hcfg-row" style={{ marginTop: "20px" }}>
        <div className="hcfg-col img">
          <h4 style={{ marginBottom: "10px" }}>Section Background Image</h4>
          {safeData.image ? (
            <img
              src={`${imagesBase}/${encodeURIComponent(safeData.image)}`}
              alt="Background preview"
              className="hcfg-preview"
              style={{ 
                maxHeight: "200px", 
                width: "100%", 
                objectFit: "cover",
                borderRadius: "4px"
              }}
            />
          ) : (
            <div className="hcfg-placeholder" style={{
              height: "150px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
              borderRadius: "4px",
              color: "#666"
            }}>
              No background image
            </div>
          )}
          <div className="hcfg-actions-inline" style={{ marginTop: "10px" }}>
            <label className="hcfg-btn" style={{
              padding: "8px 16px",
              background: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "inline-block"
            }}>
              Upload Background
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleBackgroundImageUpload(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </label>
            <button
              className="hcfg-btn danger"
              onClick={handleBackgroundImageDelete}
              style={{
                padding: "8px 16px",
                background: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "10px"
              }}
            >
              Delete Background
            </button>
          </div>
        </div>

        <div className="hcfg-col">
          <ColorPicker
            label="Background Color"
            value={safeData.backgroundColor || "#ffffff"}
            onChange={(value) =>
              setData(prev => ({
                ...prev,
                backgroundColor: value === "transparent" ? "transparent" : value
              }))
            }
            description="Background color for the section"
            allowTransparent={true}
          />
          
          <div style={{ marginTop: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Background Overlay Opacity</label>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={safeData.overlayOpacity || 0.3}
                onChange={(e) =>
                  setData(prev => ({
                    ...prev,
                    overlayOpacity: parseFloat(e.target.value),
                  }))
                }
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: "30px", textAlign: "center" }}>
                {Number(safeData.overlayOpacity || 0.3).toFixed(1)}
              </span>
            </div>
            <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
              Controls the darkness of overlay over background image (0 = no overlay, 1 = fully dark)
            </small>
          </div>
        </div>
      </div>

      {/* Color Customization for Customer Reviews */}
      <ColorSection
        title={title}
        colors={safeCustomerReviewColors}
        onColorChange={handleColorChange}
        useSectionColors={safeData.useSectionColors !== false}
        onToggleSectionColors={toggleSectionColors}
      />

      <FontSizeSection
        title={title}
        sizes={safeData.fontSizes || {}}
        onChange={handleFontSizeChange}
        useSectionFontSizes={safeData.useSectionFontSizes === true}
        onToggleSectionFontSizes={toggleSectionFontSizes}
        fields={[
          { key: "sectionTitle", label: "Section Title Size", placeholder: "e.g., 36px" },
          { key: "sectionDescription", label: "Section Description Size", placeholder: "e.g., 16px" },
          { key: "subHeading", label: "Reviewer Name Size", placeholder: "e.g., 16px" },
          { key: "content", label: "Review Text Size", placeholder: "e.g., 15px" },
        ]}
      />

      {/* Reviews List */}
      <div style={{ marginTop: "30px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h4>Customer Reviews</h4>
          <button
            onClick={addReview}
            style={{
              padding: "8px 16px",
              background: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            + Add Review
          </button>
        </div>

        {localReviews.length === 0 ? (
          <div style={{ 
            padding: "20px", 
            textAlign: "center", 
            background: "#f5f5f5", 
            borderRadius: "4px",
            color: "#666"
          }}>
            No reviews added yet. Click "Add Review" to create one.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {localReviews.map((review, index) => (
              <div key={review._id} style={{ 
                padding: "15px", 
                border: "1px solid #eee", 
                borderRadius: "4px",
                background: "#f9f9f9"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <h5 style={{ margin: 0 }}>Review #{index + 1}</h5>
                  <button
                    onClick={() => removeReview(review._id)}
                    style={{
                      padding: "4px 12px",
                      background: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    Remove
                  </button>
                </div>

                <div className="hcfg-row">
                  <div className="hcfg-col">
                    <label style={{ fontSize: "12px" }}>Customer Name</label>
                    <input
                      value={review.name || ""}
                      onChange={(e) => updateReview(review._id, "name", e.target.value)}
                      placeholder="John Doe"
                      style={{ width: "100%", padding: "6px", fontSize: "14px" }}
                    />
                  </div>

                  <div className="hcfg-col">
                    <label style={{ fontSize: "12px" }}>Designation</label>
                    <input
                      value={review.designation || ""}
                      onChange={(e) => updateReview(review._id, "designation", e.target.value)}
                      placeholder="CEO, Company Name"
                      style={{ width: "100%", padding: "6px", fontSize: "14px" }}
                    />
                  </div>

                  <div className="hcfg-col">
                    <label style={{ fontSize: "12px" }}>Rating</label>
                    <select
                      value={review.rating || "5"}
                      onChange={(e) => updateReview(review._id, "rating", e.target.value)}
                      style={{ width: "100%", padding: "6px", fontSize: "14px" }}
                    >
                      <option value="1">★☆☆☆☆ (1 Star)</option>
                      <option value="2">★★☆☆☆ (2 Stars)</option>
                      <option value="3">★★★☆☆ (3 Stars)</option>
                      <option value="4">★★★★☆ (4 Stars)</option>
                      <option value="5">★★★★★ (5 Stars)</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: "10px" }}>
                  <label style={{ fontSize: "12px", display: "block", marginBottom: "5px" }}>Review Description</label>
                  <textarea
                    rows={3}
                    value={review.description || ""}
                    onChange={(e) => updateReview(review._id, "description", e.target.value)}
                    placeholder="What the customer said..."
                    style={{ width: "100%", padding: "8px", fontSize: "14px", resize: "vertical" }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
